﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿<template>
  <div class="form-container">
    <div class="page-header">
      <div class="page-icon">📝</div>
      <h2>商品录入</h2>
    </div>
    <div class="content-card">
      <el-form ref="productForm" :model="product" :rules="rules" label-width="120px" class="form">
        <!-- 条形码 -->
        <el-form-item label="条形码" prop="barcode">
          <el-input
            v-model="product.barcode"
            placeholder="请输入或扫描条形码"
            @input="onBarcodeInput"
            ref="barcodeInput"
            clearable
          ></el-input>
        </el-form-item>

        <!-- 商品名称 -->
        <el-form-item label="商品名称" prop="product_name">
          <el-input
            v-model="product.product_name"
            placeholder="请输入商品名称"
            @input="onProductNameInput"
            clearable
          ></el-input>
        </el-form-item>

        <!-- 商品类别 -->
        <el-form-item label="商品类别" prop="category">
          <el-select v-model="product.category" placeholder="请选择商品类别">
            <el-option label="香烟" value="香烟"></el-option>
            <el-option label="茶叶" value="茶叶"></el-option>
          </el-select>
        </el-form-item>

        <!-- 销售价格 -->
        <el-form-item label="销售价格" prop="selling_price">
          <el-input
            v-model.number="product.selling_price"
            type="number"
            placeholder="请输入销售价格"
            clearable
          ></el-input>
        </el-form-item>

        <!-- 提交按钮 -->
        <el-form-item class="form-actions">
          <el-button
            type="primary"
            @click="submitForm"
            :loading="loading"
            class="submit-btn"
          >提交录入</el-button>
          <el-button @click="resetForm" class="reset-btn">重置</el-button>
        </el-form-item>

        <!-- 批量导入按钮 -->
        <el-form-item>
          <el-upload
            class="upload-btn"
            accept=".xls,.xlsx"
            :before-upload="beforeUpload"
            :show-file-list="false"
            auto-upload="false"
            ref="uploadRef"
            :action="`${API_BASE}/api/import-excel`"
            @change="handleFileChange"
          >
            <el-button type="success" @click="handleUpload">批量导入</el-button>
          </el-upload>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted} from 'vue';
import axios from 'axios';
import API_BASE from '@/api';
import { debounce } from 'lodash';
import { ElMessage, ElForm, ElFormItem, ElInput, ElButton, ElSelect, ElOption, ElUpload } from 'element-plus';

// 调用后端接口检查商品名称是否已存在
const checkProductNameExists = async (name) => {
  try {
    const response = await axios.get(`${API_BASE}/api/products/check?name=${encodeURIComponent(name)}`);
    return response.data.exists;
  } catch (error) {
    console.error('检查商品名称失败:', error);
    return false;
  }
};

const addProduct = async (productData) => {
  try {
    const response = await axios.post(`${API_BASE}/api/products`, productData);
    return response.data;
  } catch (error) {
    console.error('添加商品失败:', error);
    throw error;
  }
};

const product = ref({
  product_name: '',
  barcode: '',
  selling_price: 0,
  category: ''
});

const rules = {
  barcode: [
    { required: true, message: '请输入条形码', trigger: 'blur' }
  ],
  product_name: [
    { required: true, message: '请输入商品名称', trigger: 'blur' }
  ],
  category: [
    { required: true, message: '请选择商品类别', trigger: 'blur' }
  ],
  selling_price: [
    { required: true, message: '请输入销售价格', trigger: 'blur' },
    { type: 'number', message: '销售价格必须是数字', trigger: 'blur' }
  ]
};

const loading = ref(false);
const productForm = ref(null);
const uploadRef = ref(null);

// 防抖后的查询函数
const debouncedQueryProductByBarcode = debounce(async (barcode) => {
  if (barcode) {
    try {
      const response = await axios.get(`${API_BASE}/api/products/barcode/${barcode}`);
      if (response.status === 200 && response.data.success && response.data.product) {
        product.value.product_name = response.data.product.product_name; // 自动填充商品名称
      } else {
        ElMessage.warning('未找到对应条形码的商品');
      }
    } catch (error) {
      ElMessage.error('请求出错：' + error.message);
    }
  }
}, 600);

// 防抖监听条形码输入
const onBarcodeInput = (value) => {
  if (value && product.value.product_name === '') {
    debouncedQueryProductByBarcode(value);
  }
};

// 商品名称输入时处理 - 添加防抖检查
const debouncedCheckProductName = debounce(async (value) => {
  if (value && value.trim()) {
    const exists = await checkProductNameExists(value.trim());
    if (exists) {
      ElMessage.warning('商品名称已存在，请考虑使用其他名称');
    }
  }
}, 800);

const onProductNameInput = (value) => {
  if (value) {
    debouncedCheckProductName(value);
  }
};

// 提交商品录入表单
const submitForm = async () => {
  loading.value = true;
  try {
    await productForm.value.validate(async (valid) => {
      if (valid) {
        // 检查商品名称是否已存在
        const exists = await checkProductNameExists(product.value.product_name);
        if (exists) {
          ElMessage.error('商品名称已存在，请输入其他名称');
          loading.value = false;
          return;
        }
        
        // 调用后端接口添加商品
        const result = await addProduct(product.value);
        if (result.success) {
          ElMessage.success('商品信息录入成功');
          resetForm();
        } else {
          ElMessage.error('商品信息录入失败：' + result.message);
        }
      } else {
        loading.value = false;
        return false;
      }
    });
  } catch (error) {
    ElMessage.error('请求出错：' + error.message);
  } finally {
    loading.value = false;
  }
};

// 重置商品录入表单
const resetForm = () => {
  product.value = {
    product_name: '',
    barcode: '',
    selling_price: 0,
    category: ''
  };
};
// 处理文件变化
const handleFileChange = (file, fileList) => {
  console.log('Selected file:', file);
  console.log('File list:', fileList);

};

// 批量导入文件上传处理
const handleUpload = async () => {
  if (!uploadRef.value || !uploadRef.value.files || uploadRef.value.files.length === 0) {
    ElMessage.error('请先选择文件');
    return;
  }

  const file = uploadRef.value.files[0];  // 获取上传的文件
  const formData = new FormData();
  
  formData.append('file', file);
console.log(formData)
  loading.value = true;  // 显示加载状态
  
  try {
    const response = await axios.post(`${API_BASE}/api/import-excel`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    loading.value = false;  // 隐藏加载状态
    
   // 直接在这里弹出消息
   if (response.data.success) {
      ElMessage.success(response.data.message);  // 弹出成功消息
    } else {
      ElMessage.error(response.data.message);  // 弹出错误消息
    }
  } catch (error) {
    loading.value = false;
    ElMessage.error('批量导入失败');
    console.error('批量上传错误：', error.message);
  }
};




// 页面加载后自动聚焦条形码输入框
onMounted(() => {
  const barcodeInput = document.querySelector('input[placeholder="请输入或扫描条形码"]');
  if (barcodeInput) {
    barcodeInput.focus();  // 自动对焦条形码输入框
  }
  

});
</script>

<style scoped>
.form-container {
  padding: 0;
}

.form {
  max-width: 600px;
  margin: 0 auto;
}

.el-form-item {
  margin-bottom: 20px;
}

.el-input,
.el-select {
  width: 100%;
}

.el-button {
  width: 100%;
  padding: 12px;
  font-size: 15px;
  font-weight: 600;
  border-radius: var(--radius-sm);
  margin-top: 12px;
}

.submit-btn {
  background-color: var(--primary);
  border-color: var(--primary);
}

.submit-btn:hover {
  background-color: var(--primary-dark);
  border-color: var(--primary-dark);
}

.reset-btn {
  width: 100%;
  background-color: var(--bg-hover);
  border-color: var(--border-light);
  color: var(--text-secondary);
}

.reset-btn:hover {
  background-color: var(--border-lighter);
  border-color: var(--border-base);
}

.upload-btn {
  display: block;
  margin-top: 10px;
  width: 100%;
}

.el-form-item :deep(.el-form-item__label) {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}
</style>
